/**
 * useReviewStream — catchup-only simplification of useInvestigateStream for
 * the /reviews/:id detail page.
 *
 * There is only one mode: GET /api/reviews/{id}/stream. A PrReview row
 * always exists before the FE opens the stream (created by the GitHub
 * webhook or the manual trigger task), so unlike investigate there is no
 * live-POST-to-start path and no pre-flight clarification gate.
 *
 * State machine:
 *   idle    — initial; nothing in flight
 *   running — stream is open, review_meta received, events flowing
 *   done    — `done` event received with status='done'
 *   error   — pre-stream HTTP error OR `done` event with status='error'
 *
 * NOTE: the review `done` event's failure status is literally `'error'`
 * (not `'failed'` like investigation's SSEDonePayload) — don't reuse
 * InvestigationDetailPage's done-status branching verbatim.
 *
 * The hook owns:
 *   - the review_meta payload (repo/PR/sha/trigger)
 *   - the timeline entries (one per tool_call, mutated by tool_result —
 *     keyed on call_id so catchup-replay rows merge cleanly)
 *   - the verdict + summary_md reported by `review_submitted`
 *   - the final cost/tokens/rounds reported by `done`
 *
 * The findings TABLE is intentionally NOT populated from this hook —
 * `review_submitted`'s findings are a narrower 9-key shape
 * (SubmittedFinding, no outcome/feedback/timestamps). The detail page
 * re-fetches the full PrReviewDetail via REST once the stream reaches a
 * terminal status, exactly like InvestigationDetailPage does for
 * `snapshot.tool_calls`/system_snapshot.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { reviews } from '@/helpers/ApiHelper';
import type { TimelineEntry } from '@/components/InvestigationTimeline';
import type {
  ReviewSSEEvent,
  ReviewSnapshot,
  SSEReviewMetaPayload,
  Verdict,
} from '@/types/review';

export type ReviewStreamStatus = 'idle' | 'running' | 'done' | 'error' | 'skipped';

export interface ReviewStreamState {
  status: ReviewStreamStatus;
  reviewId: string | null;
  meta: SSEReviewMetaPayload | null;
  snapshot: ReviewSnapshot | null;
  timeline: TimelineEntry[];
  verdict: Verdict | null;
  summaryMd: string;
  degraded: boolean;
  costUsd: number;
  tokensUsed: number;
  rounds: number;
  error: { status?: number; message: string } | null;
}

export interface UseReviewStream {
  state: ReviewStreamState;
  start: (reviewId: string) => void;
  cancel: () => void;
}

const initialState: ReviewStreamState = {
  status: 'idle',
  reviewId: null,
  meta: null,
  snapshot: null,
  timeline: [],
  verdict: null,
  summaryMd: '',
  degraded: false,
  costUsd: 0,
  tokensUsed: 0,
  rounds: 0,
  error: null,
};

export function useReviewStream(): UseReviewStream {
  const [state, setState] = useState<ReviewStreamState>(initialState);
  const teardownRef = useRef<(() => void) | null>(null);

  const cancel = useCallback(() => {
    if (teardownRef.current) {
      teardownRef.current();
      teardownRef.current = null;
    }
  }, []);

  // Tear down the stream on unmount.
  useEffect(() => () => cancel(), [cancel]);

  const handleEvent = useCallback((evt: ReviewSSEEvent) => {
    setState((prev) => {
      switch (evt.event) {
        case 'review_meta': {
          const data = evt.data;
          return {
            ...prev,
            status: 'running',
            reviewId: data.review_id,
            meta: data,
          };
        }
        case 'snapshot': {
          // Idempotent: catchup replay can re-emit the persisted snapshot.
          return { ...prev, snapshot: evt.data.snapshot };
        }
        case 'tool_call': {
          const data = evt.data;
          // Idempotent: catchup replay can emit the same call_id we
          // already have in the timeline. Replace if it's there, append
          // otherwise.
          const existing = prev.timeline.findIndex(
            (row) => row.call_id === data.call_id,
          );
          const newRow: TimelineEntry = {
            call_id: data.call_id,
            round: data.round,
            tool: data.tool,
            args: data.args,
            result_preview: null,
            latency_ms: null,
            error: null,
          };
          const next =
            existing === -1
              ? [...prev.timeline, newRow]
              : prev.timeline.map((row, i) => (i === existing ? newRow : row));
          return { ...prev, timeline: next };
        }
        case 'tool_result': {
          const data = evt.data;
          return {
            ...prev,
            timeline: prev.timeline.map((row) =>
              row.call_id === data.call_id
                ? {
                    ...row,
                    result_preview: data.result_preview,
                    latency_ms: data.latency_ms,
                    error: data.error,
                  }
                : row,
            ),
          };
        }
        case 'review_submitted': {
          const data = evt.data;
          return {
            ...prev,
            verdict: data.verdict,
            summaryMd: data.summary_md,
            degraded: data.degraded,
          };
        }
        case 'done': {
          const data = evt.data;
          return {
            ...prev,
            status: data.status,
            costUsd: data.cost_usd,
            tokensUsed: data.tokens_used,
            rounds: data.rounds,
            error:
              data.status === 'error'
                ? { message: data.error ?? 'Review failed' }
                : null,
          };
        }
        default:
          return prev;
      }
    });
  }, []);

  const start = useCallback(
    (reviewId: string) => {
      cancel();
      setState({ ...initialState, status: 'running', reviewId });

      teardownRef.current = reviews.stream(reviewId, {
        onEvent: handleEvent,
        onHttpError: ({ status, detail }) => {
          setState((prev) => ({
            ...prev,
            status: 'error',
            error: { status, message: detail },
          }));
        },
        onStreamError: (err) => {
          setState((prev) => ({
            ...prev,
            status: 'error',
            error: { message: err.message },
          }));
        },
        onComplete: () => {
          // If we never got a `done` event but the stream closed, mark error.
          setState((prev) =>
            prev.status === 'running'
              ? {
                  ...prev,
                  status: 'error',
                  error: prev.error ?? { message: 'Stream ended without a done event' },
                }
              : prev,
          );
        },
      });
    },
    [cancel, handleEvent],
  );

  return { state, start, cancel };
}
